import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiDownload, FiClock, FiFileText, FiShield, FiChevronRight, FiCheck, FiZap, FiSmartphone } from 'react-icons/fi';
import logoKabupaten from '../assets/Lambang_Kabupaten_Bogor.png';

const LandingPage = () => {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Check if opened as PWA (standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIosStandalone = window.navigator.standalone === true;
    const isInApp = window.navigator.userAgent.includes('wv'); // WebView check
    
    // If opened as PWA or in standalone mode, redirect to login immediately
    if (isStandalone || isIosStandalone || isInApp) {
      console.log('✅ PWA/Standalone mode detected, redirecting to login');
      navigate('/login', { replace: true });
      return;
    }

    // Browser mode: show landing page
    setIsChecking(false);

    // Setup install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [navigate]);

  // Show loading while checking PWA status
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-100 via-pink-50 to-purple-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat...</p>
        </div>
      </div>
    );
  }

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Deteksi iOS
      const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      
      if (isIos) {
        alert('📱 Cara Install di iOS:\n\n1. Buka Safari\n2. Tap tombol Share (📤) di bagian bawah\n3. Scroll ke bawah\n4. Pilih "Add to Home Screen"\n5. Tap "Add"\n\nSetelah install, buka aplikasi dari home screen Anda!');
      } else {
        alert('📱 Cara Install di Android/Desktop:\n\n1. Chrome/Edge: Klik ikon ⋮ (menu) di pojok kanan atas\n2. Pilih "Install app" atau "Add to Home Screen"\n3. Klik "Install"\n\nJika tidak muncul opsi install:\n• Pastikan menggunakan Chrome/Edge versi terbaru\n• Refresh halaman ini (F5)\n• Atau gunakan menu browser: Settings > Install app');
      }
      return;
    }

    try {
      // Tampilkan prompt install
      await deferredPrompt.prompt();
      
      // Tunggu user memilih
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('✅ User accepted the install prompt');
        alert('✅ Aplikasi berhasil diinstall! Silakan buka dari home screen atau menu aplikasi Anda.');
      } else {
        console.log('❌ User dismissed the install prompt');
      }
      
      // Reset prompt
      setDeferredPrompt(null);
      setIsInstallable(false);
    } catch (error) {
      console.error('Install error:', error);
      
      // Fallback untuk browser yang tidak support
      const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      
      if (isIos) {
        alert('📱 Cara Install di iOS:\n\n1. Buka Safari\n2. Tap tombol Share (📤)\n3. Pilih "Add to Home Screen"\n4. Tap "Add"');
      } else {
        alert('📱 Untuk install aplikasi:\n\n1. Buka menu browser (⋮)\n2. Pilih "Install app" atau "Add to Home Screen"\n3. Ikuti petunjuk yang muncul\n\nPastikan menggunakan Chrome atau Edge untuk pengalaman terbaik.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-100 via-pink-50 to-purple-100 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Orbs */}
        <div className="absolute top-20 -left-20 w-96 h-96 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute top-40 -right-20 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-20 left-1/2 transform -translate-x-1/2 w-96 h-96 bg-rose-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        
        {/* Animated Dots */}
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-rose-400 rounded-full animate-ping opacity-60"></div>
        <div className="absolute top-3/4 right-1/4 w-2 h-2 bg-purple-400 rounded-full animate-ping opacity-60 animation-delay-1000"></div>
        <div className="absolute top-1/2 right-1/3 w-2 h-2 bg-pink-400 rounded-full animate-ping opacity-60 animation-delay-2000"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header/Navbar */}
        <header className="px-4 py-6">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="bg-white/60 backdrop-blur-lg rounded-2xl p-2 border border-rose-200/50 shadow-lg">
                <img src={logoKabupaten} alt="Logo" className="w-10 h-10" />
              </div>
              <div className="hidden sm:block">
                <h3 className="text-gray-800 font-bold text-lg">Desa Kiarapandak</h3>
                <p className="text-gray-600 text-xs">Kabupaten Bogor</p>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center md:text-left space-y-6">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-purple-100 backdrop-blur-sm text-purple-700 px-4 py-2 rounded-full border border-purple-200">
                <FiZap className="w-4 h-4" />
                <span className="text-sm font-semibold">Sistem Digital Terpercaya</span>
              </div>

              {/* Main Heading */}
              <div>
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-800 leading-normal mb-6 pb-2">
                  Layanan Surat
                  <span className="block bg-gradient-to-r from-rose-400 via-pink-500 to-purple-500 bg-clip-text text-transparent leading-normal pb-2">
                    Digital Desa
                  </span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed max-w-xl">
                  Urus administrasi surat keterangan dengan mudah, cepat, dan aman langsung dari smartphone Anda - kapanpun, dimanapun!
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleInstallClick}
                  className="group relative px-8 py-4 bg-gradient-to-r from-rose-400 via-pink-500 to-purple-500 text-white font-bold text-lg rounded-2xl shadow-2xl hover:shadow-rose-300/50 transform hover:scale-105 transition-all duration-300 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  <span className="relative flex items-center justify-center gap-3">
                    <FiDownload className="w-6 h-6" />
                    Install Aplikasi
                    <FiChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
                
                <button
                  onClick={() => navigate('/login')}
                  className="px-8 py-4 bg-white/70 backdrop-blur-lg text-rose-600 font-semibold text-lg rounded-2xl border-2 border-rose-200 hover:bg-white hover:border-rose-300 hover:shadow-lg transition-all duration-300"
                >
                  Masuk Sekarang
                </button>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center gap-6 pt-4">
                <div className="flex items-center gap-2 text-gray-600">
                  <FiCheck className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm">100% Gratis</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <FiCheck className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm">Data Aman</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <FiCheck className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm">24/7 Akses</span>
                </div>
              </div>
            </div>

            {/* Right Content - Feature Cards */}
            <div className="relative">
              {/* Main Card - Smartphone Mockup */}
              <div className="relative mx-auto max-w-sm">
                <div className="absolute inset-0 bg-gradient-to-r from-rose-300 to-purple-300 rounded-3xl blur-2xl opacity-40 animate-pulse"></div>
                <div className="relative bg-white/60 backdrop-blur-xl rounded-3xl p-8 border border-white/40 shadow-2xl">
                  <div className="space-y-6">
                    {/* Feature 1 */}
                    <div className="bg-gradient-to-br from-sky-100 to-cyan-100 backdrop-blur-sm rounded-2xl p-5 border border-sky-200 transform hover:scale-105 transition-all duration-300">
                      <div className="flex items-start gap-4">
                        <div className="bg-gradient-to-br from-sky-400 to-cyan-500 p-3 rounded-xl shadow-lg">
                          <FiClock className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h3 className="text-gray-800 font-bold text-lg mb-1">Proses Cepat</h3>
                          <p className="text-gray-600 text-sm">Pengajuan diproses dalam hitungan menit</p>
                        </div>
                      </div>
                    </div>

                    {/* Feature 2 */}
                    <div className="bg-gradient-to-br from-purple-100 to-pink-100 backdrop-blur-sm rounded-2xl p-5 border border-purple-200 transform hover:scale-105 transition-all duration-300">
                      <div className="flex items-start gap-4">
                        <div className="bg-gradient-to-br from-purple-400 to-pink-500 p-3 rounded-xl shadow-lg">
                          <FiFileText className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h3 className="text-gray-800 font-bold text-lg mb-1">Multi Jenis Surat</h3>
                          <p className="text-gray-600 text-sm">Berbagai jenis surat keterangan tersedia</p>
                        </div>
                      </div>
                    </div>

                    {/* Feature 3 */}
                    <div className="bg-gradient-to-br from-emerald-100 to-teal-100 backdrop-blur-sm rounded-2xl p-5 border border-emerald-200 transform hover:scale-105 transition-all duration-300">
                      <div className="flex items-start gap-4">
                        <div className="bg-gradient-to-br from-emerald-400 to-teal-500 p-3 rounded-xl shadow-lg">
                          <FiShield className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h3 className="text-gray-800 font-bold text-lg mb-1">Keamanan Terjamin</h3>
                          <p className="text-gray-600 text-sm">Data terenkripsi dan terlindungi</p>
                        </div>
                      </div>
                    </div>

                    {/* Feature 4 */}
                    <div className="bg-gradient-to-br from-orange-100 to-amber-100 backdrop-blur-sm rounded-2xl p-5 border border-orange-200 transform hover:scale-105 transition-all duration-300">
                      <div className="flex items-start gap-4">
                        <div className="bg-gradient-to-br from-orange-400 to-amber-500 p-3 rounded-xl shadow-lg">
                          <FiSmartphone className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h3 className="text-gray-800 font-bold text-lg mb-1">Akses Mobile</h3>
                          <p className="text-gray-600 text-sm">Gunakan di mana saja, kapan saja</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Install Info Banner */}
        {!isInstallable && (
          <div className="px-4 pb-6">
            <div className="max-w-4xl mx-auto bg-amber-50 backdrop-blur-lg border border-amber-200 rounded-2xl p-4 shadow-lg">
              <div className="flex items-start gap-3">
                <div className="bg-amber-400 p-2 rounded-lg">
                  <FiSmartphone className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="text-gray-800 font-bold mb-1">Install untuk Pengalaman Terbaik</h4>
                  <p className="text-gray-600 text-sm">
                    Gunakan Chrome/Edge untuk install otomatis, atau klik tombol "Install Aplikasi" untuk panduan lengkap
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="px-4 py-6 border-t border-gray-200">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-500 text-sm text-center md:text-left">
                © 2025 Desa Kiarapandak - Kabupaten Bogor. All rights reserved.
              </p>
              <div className="flex gap-6">
                <a href="#" className="text-gray-500 hover:text-gray-800 text-sm transition-colors">Tentang</a>
                <a href="#" className="text-gray-500 hover:text-gray-800 text-sm transition-colors">Bantuan</a>
                <a href="#" className="text-gray-500 hover:text-gray-800 text-sm transition-colors">Kontak</a>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* CSS untuk animasi */}
      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .animation-delay-1000 {
          animation-delay: 1s;
        }
        .bg-grid-pattern {
          background-image: 
            linear-gradient(to right, rgba(139, 92, 246, 0.1) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(236, 72, 153, 0.1) 1px, transparent 1px);
          background-size: 50px 50px;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
